"use client";

import { useState } from "react";
import Link from "next/link";
import { BrandIcon, BrandWordmark } from "@/components/brand";
import { useT } from "@/i18n/provider";
import { EnglishMarketingScope } from "@/components/english-scope";

const inputCls =
  "w-full rounded-lg border border-line-subtle bg-bg-base px-4 py-2.5 text-[14px] text-fg placeholder:text-fg-muted focus:outline-none focus:border-brand-400/50 focus:ring-2 focus:ring-brand-500/20";
const buttonCls =
  "w-full py-2.5 rounded-lg bg-brand-grad text-white text-[14px] font-medium shadow-glow hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed";
const linkCls =
  "text-brand-300 hover:text-brand-200 font-medium underline-offset-2 hover:underline";

export default function ForgotPasswordPage() {
  return (
    <EnglishMarketingScope>
      <ForgotPasswordBody />
    </EnglishMarketingScope>
  );
}

function ForgotPasswordBody() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch {
      // Deliberately ignore transport errors — the confirmation is uniform
      // regardless, so we never hint at whether the address exists.
    }
    setSent(true);
    setBusy(false);
  };

  return (
    <main className="min-h-screen grid-bg flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link
            href="/"
            aria-label={t("forgotPassword.backToHomeAria")}
            className="inline-flex flex-col items-center gap-4 rounded-xl px-3 py-2 -mx-3 -my-2 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 transition"
          >
            <BrandIcon size={32} />
            <h1 className="tracking-tight">
              <BrandWordmark className="text-[22px]" />
            </h1>
          </Link>
        </div>

        {sent ? (
          <div className="card p-5 text-center space-y-3">
            <h2 className="text-[15px] font-semibold text-fg-bright">{t("forgotPassword.sentTitle")}</h2>
            <p className="text-[13px] text-fg-muted">{t("forgotPassword.sentBody")}</p>
            <p className="pt-1">
              <Link href="/login" className={linkCls}>
                {t("forgotPassword.backToLogin")}
              </Link>
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="card p-5 space-y-4">
            <div className="text-center mb-1">
              <h2 className="text-[15px] font-semibold text-fg-bright">{t("forgotPassword.title")}</h2>
              <p className="text-[12px] text-fg-muted mt-1">{t("forgotPassword.subtitle")}</p>
            </div>

            <div>
              <label className="text-[11px] text-fg-muted mb-1.5 block">{t("forgotPassword.emailLabel")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("forgotPassword.emailPlaceholder")}
                className={inputCls}
                autoFocus
                required
              />
            </div>

            <button type="submit" disabled={busy || !email} className={buttonCls}>
              {busy ? t("forgotPassword.sending") : t("forgotPassword.submit")}
            </button>

            <p className="text-center text-[12px] text-fg-muted">
              <Link href="/login" className={linkCls}>
                {t("forgotPassword.backToLogin")}
              </Link>
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
